import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/contexts/ConfigContext';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/services/auth';
import { LogIn } from 'lucide-react';

export function Login() {
  const { isAuthenticated, login, isLoading } = useAuth();
  const { config } = useConfig();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect URL from state, or default to home
  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    // If already authenticated, redirect
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleLogin = () => {
    // Clear any previous logout flag
    authService.clearLogoutFlag();
    login();
  };

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={config.portal_name || 'Product Portal'}
              className="mx-auto h-16 w-auto"
            />
          ) : (
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {config.portal_name || 'Product Portal'}
            </h1>
          )}
          <h2 className="mt-6 text-xl font-semibold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Use your organization credentials to access the portal
          </p>
        </div>

        <div className="mt-8 bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLogin}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <LogIn className="mr-2 h-5 w-5" />
            Sign in with SSO
          </button>

          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            By signing in, you agree to the terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
